import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  Button,
  IconButton,
  Card,
  CardContent,
  Divider,
  Snackbar,
  Alert
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';

const ContactModal = ({ open, onClose, contactInfo = { email: 'kyle7949@sa21c.com', phone: '010-8626-2111', isIframe: false } }) => {
  const [snackbarOpen, setSnackbarOpen] = React.useState(false);
  const [snackbarMessage, setSnackbarMessage] = React.useState('');

  const copyToClipboard = (text, type) => {
    navigator.clipboard.writeText(text).then(() => {
      setSnackbarMessage(`${type}이(가) 클립보드에 복사되었습니다!`);
      setSnackbarOpen(true);
    }).catch(() => {
      setSnackbarMessage('복사에 실패했습니다.');
      setSnackbarOpen(true);
    });
  };

  const handleEmailClick = () => {
    window.location.href = `mailto:${contactInfo.email}`;
  };

  const handlePhoneClick = () => {
    window.location.href = `tel:${contactInfo.phone}`;
  };

  return (
    <>
      <Dialog 
        open={open} 
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            background: 'linear-gradient(135deg, #FFFFFF 0%, #F8F9FA 100%)',
            maxHeight: { xs: '90vh', md: '80vh' },
            margin: { xs: 1, md: 2 }
          }
        }}
      >
        <DialogTitle sx={{ 
          textAlign: 'center', 
          pb: { xs: 1, md: 1 },
          background: 'linear-gradient(135deg, #1565C0 0%, #37474F 100%)',
          color: 'white',
          position: 'relative',
          p: { xs: 2, md: 3 }
        }}>
          <BusinessCenterIcon sx={{ 
            fontSize: { xs: 32, md: 40 }, 
            mb: { xs: 0.5, md: 1 }, 
            display: 'block', 
            mx: 'auto' 
          }} />
          <Typography variant="h5" sx={{ 
            fontWeight: 800,
            textShadow: '0 2px 4px rgba(0,0,0,0.3)',
            color: 'white',
            fontSize: { xs: '1.2rem', md: '1.5rem' }
          }}>
            도입 상담 문의
          </Typography>
          <Typography variant="body2" sx={{ 
            opacity: 1, 
            mt: { xs: 0.5, md: 1 },
            fontWeight: 500,
            textShadow: '0 1px 2px rgba(0,0,0,0.2)',
            color: 'white',
            fontSize: { xs: '0.8rem', md: '0.9rem' },
            lineHeight: 1.4
          }}>
            {contactInfo.isIframe 
              ? "영업 담당자를 통한 전문 상담을 받아보세요" 
              : "AI 기반 질의응답 시스템 도입에 대한 상담을 받아보세요"
            }
          </Typography>
          <IconButton
            onClick={onClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: 'white'
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: { xs: 2, md: 4 }, overflow: 'auto' }}>
          <Typography variant="h6" gutterBottom sx={{ 
            textAlign: 'center', 
            color: 'primary.main',
            mb: { xs: 2, md: 4 },
            mt: { xs: 1, md: 3 },
            fontWeight: 600,
            fontSize: { xs: '1rem', md: '1.2rem' }
          }}>
            📞 연락처 정보
          </Typography>

          {/* 이메일 카드 */}
          <Card sx={{ mb: { xs: 1.5, md: 2 }, border: '1px solid #E3F2FD' }}>
            <CardContent sx={{ p: { xs: 2, md: 3 } }}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: { xs: 'flex-start', sm: 'center' }, 
                justifyContent: 'space-between',
                flexDirection: { xs: 'column', sm: 'row' },
                gap: { xs: 2, sm: 0 }
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0 }}>
                  <EmailIcon sx={{ color: '#1565C0', mr: { xs: 1.5, md: 2 }, fontSize: { xs: 24, md: 28 } }} />
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', md: '0.9rem' } }}>
                      이메일
                    </Typography>
                    <Typography variant="h6" sx={{ 
                      fontWeight: 600, 
                      color: '#1565C0',
                      fontSize: { xs: '0.9rem', md: '1rem' },
                      wordBreak: 'break-all'
                    }}>
                      {contactInfo.email}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: { xs: 0.5, md: 1 }, flexShrink: 0 }}>
                  <IconButton 
                    onClick={() => copyToClipboard(contactInfo.email, '이메일')}
                    size="small"
                    sx={{ 
                      backgroundColor: '#E3F2FD',
                      '&:hover': { backgroundColor: '#BBDEFB' },
                      minWidth: { xs: 32, md: 40 },
                      height: { xs: 32, md: 40 }
                    }}
                  >
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleEmailClick}
                    sx={{ 
                      minWidth: 'auto',
                      px: { xs: 1.5, md: 2 },
                      py: { xs: 0.5, md: 1 },
                      fontSize: { xs: '0.7rem', md: '0.8rem' },
                      backgroundColor: '#1565C0',
                      '&:hover': { backgroundColor: '#0D47A1' }
                    }}
                  >
                    메일 보내기
                  </Button>
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* 전화번호 카드 */}
          <Card sx={{ mb: { xs: 2, md: 3 }, border: '1px solid #E8F5E8' }}>
            <CardContent sx={{ p: { xs: 2, md: 3 } }}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: { xs: 'flex-start', sm: 'center' }, 
                justifyContent: 'space-between',
                flexDirection: { xs: 'column', sm: 'row' },
                gap: { xs: 2, sm: 0 }
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0 }}>
                  <PhoneIcon sx={{ color: '#2E7D32', mr: { xs: 1.5, md: 2 }, fontSize: { xs: 24, md: 28 } }} />
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', md: '0.9rem' } }}>
                      전화번호
                    </Typography>
                    <Typography variant="h6" sx={{ 
                      fontWeight: 600, 
                      color: '#2E7D32',
                      fontSize: { xs: '0.9rem', md: '1rem' },
                      wordBreak: 'break-all'
                    }}>
                      {contactInfo.phone}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: { xs: 0.5, md: 1 }, flexShrink: 0 }}>
                  <IconButton 
                    onClick={() => copyToClipboard(contactInfo.phone, '전화번호')}
                    size="small"
                    sx={{ 
                      backgroundColor: '#E8F5E8',
                      '&:hover': { backgroundColor: '#C8E6C9' },
                      minWidth: { xs: 32, md: 40 },
                      height: { xs: 32, md: 40 }
                    }}
                  >
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handlePhoneClick}
                    sx={{ 
                      minWidth: 'auto',
                      px: { xs: 1.5, md: 2 },
                      py: { xs: 0.5, md: 1 },
                      fontSize: { xs: '0.7rem', md: '0.8rem' },
                      backgroundColor: '#2E7D32',
                      '&:hover': { backgroundColor: '#1B5E20' }
                    }}
                  >
                    전화 걸기
                  </Button>
                </Box>
              </Box>
            </CardContent>
          </Card>

          <Divider sx={{ my: { xs: 2, md: 3 } }} />

          <Box sx={{ 
            backgroundColor: '#F5F5F5', 
            p: { xs: 2, md: 3 }, 
            borderRadius: 2,
            textAlign: 'center'
          }}>
            <Typography variant="body1" sx={{ 
              mb: { xs: 1, md: 2 }, 
              fontWeight: 600,
              fontSize: { xs: '0.9rem', md: '1rem' }
            }}>
              🚀 빠른 도입 프로세스
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ 
              lineHeight: 1.6,
              fontSize: { xs: '0.8rem', md: '0.9rem' }
            }}>
              • 상담 신청 → 데모 시연 → 맞춤 제안서 → 계약<br />
              • 평균 1-2주 내 시스템 구축 완료<br />
              • 전체 소스코드 제공으로 완전한 기술 검증 가능
            </Typography>
          </Box>
        </DialogContent>

        <DialogActions sx={{ 
          p: { xs: 2, md: 3 }, 
          pt: 0,
          justifyContent: 'center'
        }}>
          <Button 
            onClick={onClose} 
            variant="outlined"
            sx={{ 
              px: { xs: 3, md: 4 },
              py: { xs: 1, md: 1.5 },
              fontSize: { xs: '0.8rem', md: '0.9rem' },
              borderColor: '#9E9E9E',
              color: '#616161',
              minWidth: { xs: 80, md: 100 }
            }}
          >
            닫기
          </Button>
        </DialogActions>
      </Dialog>

      {/* 복사 완료 스낵바 */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbarOpen(false)} 
          severity="success" 
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ContactModal; 